import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Check, MapPin, Clock, FileText, Landmark, Loader2, ArrowLeft } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import ComplianceNote from "@/components/ComplianceNote";
import Disclaimer from "@/components/Disclaimer";
import PricingBreakdown from "@/components/PricingBreakdown";
import { timelineText, hasTimeline, TIMELINE_QUALIFIER } from "@/lib/timeline";
import CountryFacts from "@/pages/CountryFacts";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { IMAGES, countryImage } from "@/lib/images";
import { setSeo, setJsonLd, clearJsonLd, resetSeo, SITE_URL } from "@/lib/seo";
import { getDisplayConfig, enabledFields } from "@/lib/displayConfig";

export default function CountryDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [factFields, setFactFields] = useState([]);

  useEffect(() => {
    setLoading(true);
    const lng = i18n.language || "en";
    const suffix = lng && lng !== "en" ? `?lang=${encodeURIComponent(lng)}&preview=1` : "";
    api.get(`/country/${slug}${suffix}`).then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
    getDisplayConfig().then((cfg) => setFactFields(enabledFields(cfg, "snippet_country")));
  }, [slug, i18n.language]);

  useEffect(() => {
    if (!data) return;
    const feeTxt = formatUSD(data.setup_fee);
    const cname = t(`country.${data.slug}.name`, { defaultValue: data.name });
    const cloc = t(`country.${data.slug}.nameLoc`, { defaultValue: data.name });
    const tl = (data.timeline || "").trim() || t("country_ui.timelineDefault", { defaultValue: "Timeline confirmed after preliminary review" });
    setSeo({
      title: t(`seo.country.${data.slug}.title`, { name: cname, loc: cloc, defaultValue: `Company Formation in ${data.name} — Fees, Requirements & Banks | UNICORP` }),
      description: t(`seo.country.${data.slug}.desc`, { name: cname, loc: cloc, fee: feeTxt, timeline: tl, defaultValue: `Register a company in ${data.name} from ${feeTxt} all-inclusive. ${timelineText(data.timeline)}. See requirements, banking options and FAQs. Remote onboarding with UNICORP.` }),
      image: countryImage(data.slug),
      url: window.location.href,
    });
    if (data.faqs?.length) {
      setJsonLd("faq-schema", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      });
    }
    setJsonLd("country-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Company Formation", item: `${SITE_URL}/company-formation` },
        { "@type": "ListItem", position: 3, name: data.name, item: `${SITE_URL}/company-formation/${data.slug}` },
      ],
    });
    return () => { clearJsonLd("faq-schema"); clearJsonLd("country-breadcrumb"); resetSeo(); };
  }, [data, t]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;
  if (notFound || !data) return (
    <div className="min-h-screen"><Navbar /><div className="max-w-3xl mx-auto px-5 pt-40 pb-24 text-center"><h1 className="font-heading text-3xl text-primary">{t("country_ui.notFoundTitle")}</h1><Button className="mt-6 rounded-sm bg-primary text-primary-foreground" onClick={() => navigate("/company-formation")}>{t("country_ui.browseAll")}</Button></div><Footer /></div>
  );

  const cname = t(`country.${data.slug}.name`, { defaultValue: data.name });
  const cloc = t(`country.${data.slug}.nameLoc`, { defaultValue: data.name });
  const cfor = t(`country.${data.slug}.nameFor`, { defaultValue: data.name });
  const tt = (v) => (v || "").trim() || t("country_ui.timelineDefault", { defaultValue: "Timeline confirmed after preliminary review" });
  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const start = () => navigate(`/start?pkg=inc_${data.slug}`);
  const custom = data.confirmation_status === "requires_confirmation";
  const reviewed = data.review_status === "reviewed" && data.compliance_reviewed_at;
  const recBank = data.banks.length
    ? [...data.banks].sort((a, b) => (a.type !== "Digital") - (b.type !== "Digital") || a.fee - b.fee)[0]
    : null;
  const bundleStart = (bankName) => navigate(`/start?pkg=inc_${data.slug}&bank=${encodeURIComponent(bankName)}`);

  return (
    <div className="min-h-screen">
      <Navbar />
      {/* Hero */}
      <section className="relative grain overflow-hidden border-b border-border pt-28">
        <div className="absolute inset-0"><img src={countryImage(data.slug)} alt={data.name} decoding="async" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-b from-background/75 via-background/90 to-background" /></div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-3 gap-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2">
            <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4" data-testid="country-breadcrumb">
              <button onClick={() => navigate("/company-formation")} className="hover:text-orange-700">{t("country_ui.companyFormation")}</button>
              <span>/</span><span className="text-foreground">{cname}</span>
            </nav>
            <button onClick={() => navigate("/company-formation")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5"><ArrowLeft className="h-4 w-4" /> {t("country_ui.allCountries")}</button>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-600"><MapPin className="h-3.5 w-3.5" /> {t("country_ui.companyFormation")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary flex items-center gap-3">{data.flag && data.flag.length <= 4 && <span className="text-4xl sm:text-5xl">{data.flag}</span>}{cname}</h1>
            {data.tagline && <p className="mt-2 text-orange-700 font-medium">{data.tagline}</p>}
            {(data.hero_description || data.overview) && <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-2xl">{data.hero_description || data.overview}</p>}
            <div className="mt-5 max-w-2xl"><ComplianceNote /></div>
            <div className="mt-3 max-w-2xl"><Disclaimer type="compliance" /></div>
            {reviewed && <p className="mt-2 text-[11px] text-muted-foreground" data-testid="country-last-reviewed">{t("country_ui.lastReviewed", { date: data.compliance_reviewed_at })}</p>}
            <div className="mt-6 flex flex-wrap gap-6 text-sm">
              <span className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 text-orange-600" /> {tt(data.timeline)}</span>
              <span className="flex items-center gap-2 text-muted-foreground"><Landmark className="h-4 w-4 text-primary" /> {t("country_ui.bankOptions", { count: data.banks.length })}</span>
            </div>
          </motion.div>

          {/* Fee card */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="rounded-2xl border border-border bg-card p-6 card-soft h-fit lg:sticky lg:top-28" data-testid="country-fee-card">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("country_ui.setupFee")}</p>
            <p className="mt-1 font-heading text-4xl tracking-tight text-primary">{custom ? t("country_ui.customQuotation") : formatUSD(data.setup_fee)}</p>
            <PricingBreakdown serviceFee={data.setup_fee} govFeeStatus={data.gov_fee_status} annualFee={data.annual_fee} renewalRule={data.renewal_rule} custom={custom} />
            {data.pricing_conditions && <p className="mt-3 text-[11px] text-muted-foreground">{data.pricing_conditions}</p>}
            <Button onClick={custom ? () => navigate("/contact") : start} className="mt-5 w-full rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="country-start">{custom ? t("country_ui.requestQuotation") : t("country_ui.getAssistance")} <ArrowRight className="h-4 w-4 ml-1" /></Button>
            <Disclaimer type="pricing" className="mt-3" />
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="timeline-qualifier">{t("country_ui.timelineQualifier", { defaultValue: TIMELINE_QUALIFIER.formation })}</p>
            {(data.addons?.length > 0 || data.banks?.length > 0) && (
              <div className="mt-5 border-t border-border pt-4" data-testid="frequently-added">
                <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("country_ui.frequentlyAdded", { name: cname, forp: cfor })}</p>
                <ul className="mt-2.5 space-y-1.5 text-sm">
                  {data.banks?.length > 0 && (
                    <li><button onClick={start} className="text-orange-700 hover:underline" data-testid="freq-banking">{t("country_ui.bankingAssistance")}</button></li>
                  )}
                  {(data.addons || []).slice(0, 4).map((a) => (
                    <li key={a.id || a.name} className="flex justify-between gap-2"><span className="text-muted-foreground">{t(`wizard.addon.${slugify(a.name)}`, { defaultValue: a.name })}</span><span className="font-mono text-xs text-foreground">{formatUSD(a.price)}</span></li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">{t("country_ui.optionalNote")}</p>
              </div>
            )}
            {recBank && (
              <button onClick={() => bundleStart(recBank.name)} className="mt-4 w-full rounded-lg border border-orange-200 bg-orange-50 p-3 text-left hover:border-orange-300 transition-colors" data-testid="country-bundle-nudge">
                <p className="text-xs font-medium text-orange-800 flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5" /> {t("country_ui.bundleSave", { bank: recBank.name })}</p>
                <p className="mt-0.5 text-[11px] text-orange-700">{t("country_ui.bundleDesc", { type: t(recBank.type === "Digital" ? "country_ui.typeFintech" : "country_ui.typeBank") })}</p>
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Intro copy */}
      {data.intro?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl text-primary">{t("country_ui.whyIncorporate", { name: cname, loc: cloc })}</h2>
            <div className="mt-5 space-y-4">
              {data.intro.map((p, i) => <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>)}
            </div>
          </div>
        </section>
      )}

      {/* Body */}
      {(data.whats_included?.length > 0 || data.requirements?.length > 0) && (
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-10">
        {data.whats_included?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-7 card-soft">
          <h2 className="font-heading text-2xl text-primary">{t("country_ui.whatsIncluded")}</h2>
          <ul className="mt-5 space-y-3">{data.whats_included.map((f) => (<li key={f} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-orange-100 shrink-0"><Check className="h-3 w-3 text-orange-700" /></span>{f}</li>))}</ul>
        </div>
        )}
        {data.requirements?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-7 card-soft">
          <h2 className="font-heading text-2xl text-primary">{t("country_ui.requirements")}</h2>
          <ul className="mt-5 space-y-3">{data.requirements.map((r) => (<li key={r} className="flex items-start gap-2.5 text-sm text-slate-700"><FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />{r}</li>))}</ul>
        </div>
        )}
      </section>
      )}

      {/* Key facts (admin-controlled snippet fields) */}
      {(() => {
        const factVal = (k) => {
          if (k === "setup_fee") return formatUSD(data.setup_fee);
          if (k === "annual_fee") return `${formatUSD(data.annual_fee)}${t("country_ui.perYear", { defaultValue: "/yr" })}`;
          if (k === "timeline") return tt(data.timeline);
          if (k === "bank_count") return t("country_ui.options", { count: data.banks.length });
          return data[k];
        };
        const tiles = factFields.map((f) => ({ ...f, val: factVal(f.key) })).filter((f) => f.val);
        if (!tiles.length) return null;
        return (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14" data-testid="country-keyfacts">
            <h2 className="font-heading text-2xl text-primary mb-6">{t("country_ui.keyFacts")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {tiles.map((f) => (
                <div key={f.key} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`country-fact-${f.key}`}>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t(`country_ui.factLabel.${f.key}`, { defaultValue: f.label })}</p>
                  <p className="mt-1.5 font-heading text-lg text-primary">{f.val}</p>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Key benefits & banking options (admin content) */}
      {(data.key_benefits?.length > 0 || data.banking_options?.length > 0) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 grid lg:grid-cols-2 gap-10">
          {data.key_benefits?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-7 card-soft" data-testid="country-benefits">
              <h2 className="font-heading text-2xl text-primary">{t("country_ui.keyBenefits")}</h2>
              <ul className="mt-5 space-y-3">{data.key_benefits.map((b, i) => (<li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-green-100 shrink-0"><Check className="h-3 w-3 text-green-700" /></span>{b}</li>))}</ul>
            </div>
          )}
          {data.banking_options?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-7 card-soft" data-testid="country-banking-options">
              <h2 className="font-heading text-2xl text-primary">{t("country_ui.bankingOptionsTitle")}</h2>
              <ul className="mt-5 space-y-3">{data.banking_options.map((b, i) => (<li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><Landmark className="h-4 w-4 mt-0.5 text-primary shrink-0" />{b}</li>))}</ul>
            </div>
          )}
        </section>
      )}

      {data.restrictions && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14" data-testid="country-restrictions">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
            <FileText className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div><p className="text-sm font-medium text-amber-900">{t("country_ui.restrictionsTitle")}</p><p className="mt-1 text-sm text-amber-900/90 leading-relaxed">{data.restrictions}</p></div>
          </div>
        </section>
      )}

      {/* Jurisdiction-specific verified content (priority countries) */}
      <CountryFacts slug={data.slug} facts={data.facts} />

      {/* Process timeline */}
      {data.process?.length > 0 && (
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <h2 className="font-heading text-2xl text-primary mb-8">{t("country_ui.howItWorks")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.process.map((p, i) => (
            <div key={p.step} className="rounded-xl border border-border bg-card p-6 card-soft">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground font-mono text-sm">{i + 1}</span>
              <h3 className="mt-4 font-heading text-lg text-primary">{p.step}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.detail}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Banks */}
      {data.banks.length > 0 && data.banks_visible !== false && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-heading text-2xl text-primary mb-2">{t("country_ui.bankingIn", { name: cname, loc: cloc })}</h2>
          <p className="text-sm text-muted-foreground mb-8">{t("country_ui.bankingBlurb")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.banks.map((b) => {
              const isRec = recBank && b.name === recBank.name && b.fee === recBank.fee;
              return (
                <div key={b.name} className={`relative rounded-xl border bg-card p-5 card-soft flex flex-col gap-3 ${isRec ? "border-orange-400 ring-1 ring-orange-200" : "border-border"}`} data-testid={`country-bank-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "")}`}>
                  {isRec && <span className="absolute -top-2.5 left-4 rounded-sm bg-orange-600 text-white text-[10px] tracking-wider uppercase px-2 py-0.5">{t("country_ui.recommended")}</span>}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3"><span className="grid place-items-center h-9 w-9 rounded-sm bg-secondary text-primary"><Landmark className="h-4 w-4" /></span><div><p className="text-sm text-foreground">{b.name}</p><p className="text-xs text-muted-foreground">{t(`country_ui.bankType.${b.type}`, { defaultValue: b.type })}</p></div></div>
                    <span className="font-mono text-sm text-orange-700">{formatUSD(b.fee)}</span>
                  </div>
                  <Button variant="outline" onClick={() => bundleStart(b.name)} className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-8 text-xs" data-testid={`country-bank-bundle-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "")}`}>{t("country_ui.addSave")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* FAQs */}
      {data.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-heading text-2xl sm:text-3xl text-primary mb-6">{t("country_ui.faqTitle")}</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="country-faqs">
            {data.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-foreground hover:text-orange-700 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Add-ons + CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-2xl bg-primary text-primary-foreground px-8 py-12 text-center grain">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">{t("country_ui.readyToLaunch", { name: cname, loc: cloc })}</h2>
          <p className="mt-3 text-primary-foreground/70">{t("country_ui.setupFrom", { fee: formatUSD(data.setup_fee) })}{hasTimeline(data.timeline) ? ` · ${t("country_ui.deliveredIn", { timeline: data.timeline })}` : ""}.</p>
          <Button size="lg" className="mt-7 rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-8" onClick={start} data-testid="country-cta">{t("country_ui.getAssistance")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
